// CollectionsPage — Curated stock collections
// Layout (matches attached photo):
//   - Hero header with light gradient
//   - List of collection cards (Tech Leaders, Cloud & Software, AI & Chips)
//   - Each card: title + count + brand icon strip + colored circular icon

import { useNavigate } from 'react-router-dom';
import { COLLECTIONS, STOCKS } from '../../data/stocks';

export function CollectionsPage() {
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="relative overflow-hidden px-4 pb-6 pt-12" style={{ background: 'linear-gradient(180deg, #EEF1FF 0%, #F7F8FF 50%, #FFFFFF 100%)' }}>
        <h1 className="text-[28px] font-bold tracking-tight text-tf-text-1">Collections</h1>
        <p className="mt-1 text-[14px] text-tf-text-3">Curated baskets of stocks grouped by theme.</p>
      </div>

      {/* Filter pills */}
      <div className="px-4 pt-2 pb-3 flex items-center gap-2">
        <button className="rounded-full bg-tf-text-1 px-4 py-1.5 text-[13px] font-semibold text-white" data-testid="filter-stock">Stock</button>
        <button className="rounded-full bg-tf-surface-1 px-4 py-1.5 text-[13px] font-semibold text-tf-text-1" data-testid="filter-etfs">ETFs</button>
      </div>

      {/* Collection list */}
      <div className="px-4 pb-6 space-y-3">
        {COLLECTIONS.map((c) => {
          const collectionStocks = STOCKS.filter((s) => s.collections.includes(c.id));
          return (
            <button
              key={c.id}
              onClick={() => nav('/portal/discover', { state: { collectionId: c.id } })}
              data-testid={`collection-${c.id}`}
              className={`relative flex w-full items-center justify-between overflow-hidden rounded-[16px] border border-tf-border bg-white p-4 text-left active:scale-[0.99] transition`}
            >
              <div className="flex-1">
                <div className="text-[16px] font-bold text-tf-text-1">{c.name}</div>
                <div className="text-[12px] text-tf-text-3">{c.count} stocks</div>
                {/* Brand icon strip */}
                <div className="mt-3 flex items-center gap-1.5">
                  {collectionStocks.slice(0, 6).map((s) => (
                    <img key={s.id} src={s.icon} alt={s.name} className="h-6 w-6 rounded-full border-2 border-white" />
                  ))}
                </div>
              </div>
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/80 backdrop-blur-sm shadow-sm">
                <CollectionIcon kind={c.icon} />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CollectionIcon({ kind }: { kind: string }) {
  if (kind === 'tech') {
    return (
      <svg viewBox="0 0 24 24" className="h-7 w-7 text-[#7E9AFF]" fill="none" stroke="currentColor" strokeWidth="1.6">
        <rect x="4" y="7" width="16" height="11" rx="1.5" /><path d="M2 12h2M20 12h2M9 21h6M12 18v3" strokeLinecap="round" />
        <circle cx="12" cy="12.5" r="2.5" />
      </svg>
    );
  }
  if (kind === 'cloud') {
    return (
      <svg viewBox="0 0 24 24" className="h-7 w-7 text-tf-info" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d="M7 18a4 4 0 010-8 5 5 0 019.6-1.5A4 4 0 0117 18H7z" strokeLinejoin="round" />
        <circle cx="10" cy="14" r="1" fill="currentColor" />
        <circle cx="14" cy="14" r="1" fill="currentColor" />
        <circle cx="12" cy="16" r="1" fill="currentColor" />
      </svg>
    );
  }
  if (kind === 'chip') {
    return (
      <svg viewBox="0 0 24 24" className="h-7 w-7 text-[#FF6B9A]" fill="none" stroke="currentColor" strokeWidth="1.6">
        <rect x="6" y="6" width="12" height="12" rx="1" />
        <path d="M9 3v3M15 3v3M9 18v3M15 18v3M3 9h3M3 15h3M18 9h3M18 15h3" strokeLinecap="round" />
        <rect x="9" y="9" width="6" height="6" rx="0.5" />
      </svg>
    );
  }
  return null;
}
